'use client';

/**
 * @fileoverview `HostTransitionMount` — the single per-shell mount of the DS
 * 1.4.0 `LayerTransition` atom, wired into the cross-host switch as an **enter**
 * transition (WEB-473, slice of WEB-463).
 *
 * Host switches (Apex ⇄ City ⇄ App ⇄ Studio) are cross-host hard navigations,
 * so the destination host plays a single one-shot crossfade on arrival rather
 * than intercepting the (native, cross-host) `<a>` to play an exit animation —
 * see `useHostTransition` for the full rationale. This keeps the Wayfinder's
 * native anchors untouched and adds no navigation-timing hacks.
 *
 * Presentational + additive: it observes whether we just crossed hosts via
 * `useHostTransition`, renders the atom in `oneShot` mode as a full-screen
 * passive overlay, and unmounts itself when the run finishes (`onDone`). SSR-safe
 * (renders `null` on the server and first client paint) and reduced-motion-aware
 * (the hook never activates under reduced motion, so the overlay never mounts).
 *
 * Each shell (apex / city / app / studio) renders this once and passes its own
 * `currentLayer` — App and Studio share the `app.loci.city` host and can only be
 * distinguished by the shell that knows which one it is.
 */

import * as React from 'react';
import type { CityCode } from '@loci/city-editions';

import { LayerTransition } from '@/components/atoms/motion/LayerTransition';
import { type HostLayer } from '@/lib/host-layer';
import { useHostTransition } from '@/hooks/useHostTransition';

export interface HostTransitionMountProps {
  /** The layer of the surface mounting this transition (apex / city / app / studio). */
  currentLayer: HostLayer;
  /** Resolved city code, used to specialise the destination sub-line. */
  cityCode?: CityCode;
}

export function HostTransitionMount({
  currentLayer,
  cityCode,
}: HostTransitionMountProps): React.ReactElement | null {
  const nodes = useHostTransition({ currentLayer, cityCode });
  const [done, setDone] = React.useState(false);

  const handleDone = React.useCallback(() => setDone(true), []);

  if (!nodes || done) return null;

  // Position on a WRAPPER, not the atom root: `.lm-layers` sets
  // `position: relative` from `@layer motion`, which is registered AFTER
  // Tailwind's `utilities` (WEB-857), so it still wins over `.fixed` — the
  // constraint survived that refactor unchanged. A `fixed` class on the atom
  // itself would be overridden back to `relative`. The wrapper is a full-screen fixed
  // overlay pinned above all chrome; `pointer-events-none` keeps it passive so
  // it never intercepts clicks on the page underneath. The atom fills the
  // wrapper (width/height 100%) so the crossfade is full-bleed.
  return (
    <div
      data-testid="host-transition-mount"
      aria-hidden="true"
      // WEB-637: above AppHeader (`--z-chrome`: 60) via the scrim rung.
      className="pointer-events-none fixed inset-0 z-scrim"
    >
      <LayerTransition
        oneShot
        onDone={handleDone}
        from={nodes.from}
        to={nodes.to}
        width="100%"
        height="100%"
        className="lm-layers--flush"
      />
    </div>
  );
}
